import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tiles", "panel", "display"]
  static values = {
    submitUrl: String,
    minLength: { type: Number, default: 4 },
    maxLength: { type: Number, default: 6 },
    membershipId: Number
  }

  connect() {
    this.pin = ""
    this._keyHandler = (e) => this.handleKey(e)
    document.addEventListener("keydown", this._keyHandler)
    this.focusFirstTile()
  }

  disconnect() {
    document.removeEventListener("keydown", this._keyHandler)
  }

  focusFirstTile() {
    if (!this.hasTilesTarget) return
    const first = this.tilesTarget.querySelector("button")
    if (first) requestAnimationFrame(() => first.focus())
  }

  selectMembership(event) {
    this.membershipIdValue = Number(event.params.membershipId)
    if (this.hasTilesTarget) this.tilesTarget.hidden = true
    if (this.hasPanelTarget) this.panelTarget.hidden = false
    this.pin = ""
    this.render()
  }

  back() {
    if (this.hasTilesTarget) this.tilesTarget.hidden = false
    if (this.hasPanelTarget) this.panelTarget.hidden = true
    this.pin = ""
    this.focusFirstTile()
  }

  press(event) {
    if (this.pin.length >= this.maxLengthValue) return
    this.pin += String(event.params.digit)
    this.render()
    if (this.pin.length >= this.minLengthValue) this.maybeSubmit()
  }

  backspace() {
    this.pin = this.pin.slice(0, -1)
    this.render()
  }

  clear() {
    this.pin = ""
    this.render()
  }

  handleKey(e) {
    if (!this.hasPanelTarget || this.panelTarget.hidden) return
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault()
      this.press({ params: { digit: e.key } })
    } else if (e.key === "Backspace") {
      e.preventDefault(); this.backspace()
    } else if (e.key === "Escape") {
      e.preventDefault(); this.back()
    }
  }

  render() {
    if (this.hasDisplayTarget) this.displayTarget.textContent = "•".repeat(this.pin.length)
  }

  maybeSubmit() {
    if (!this.membershipIdValue) return

    // No submit URL means somebody else owns what happens next — the manager
    // approval flow, which used to reach in and overwrite this method on the
    // instance. That patch was silently lost whenever Stimulus rebuilt the
    // controller (a Turbo morph re-inserting the modal is enough), and the
    // original method's early return then swallowed the PIN without a word.
    // Announcing it leaves nothing to overwrite.
    if (!this.submitUrlValue) {
      this.element.dispatchEvent(new CustomEvent("pin-keypad:entered", {
        detail: { membershipId: this.membershipIdValue, pin: this.pin, keypad: this },
        bubbles: true
      }))
      return
    }
    // Submit via a real form so Turbo handles the POST → redirect →
    // flash chain natively (a raw fetch consumes the session flash on
    // its redirect-follow before any re-render gets to read it).
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.submitUrlValue
    form.style.display = "none"
    const fields = {
      membership_id: this.membershipIdValue,
      pin: this.pin,
      authenticity_token: csrf || ""
    }
    for (const [k, v] of Object.entries(fields)) {
      const input = document.createElement("input")
      input.name = k
      input.value = v
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.requestSubmit()
  }
}
